// The single door to checklist storage.
//
// Components read and write config through this module and never talk to the
// API directly. One place to validate what comes back, one place to handle the
// network being down, one place to change if the shape evolves.
//
// Storage used to be localStorage, per-browser and per-origin. It is now the
// /checklists endpoint in Longview-Api, backed by the Omni database. That is a
// deliberate reversal of spec s6: the field description is what people tune
// when extraction keeps missing something, and tuning it in localStorage helped
// one person on one machine until they cleared site data. Checklists are a
// company asset, so they live where the company can see them.
//
// Consequences of the move, all of them intended:
//   - Checklists are shared. Editing one changes it for everyone.
//   - There is no offline mode. No endpoint, no checklists.
//   - Field ids are assigned by the server and are not stable across a save.

import { endpoints } from './endpoints';
import { PRICING_BUILTIN_KEY, PRICING_CONFIG_NAME, PRICING_FIELDS } from '../pricing/scope';
import {
  LEGACY_STORAGE_KEY,
  STORAGE_VERSION,
  type ChecklistConfig,
  type ChecklistField,
  type ConfigBlob,
  type FieldType,
} from './types';

const EMPTY: ConfigBlob = { selectedConfigId: null, configs: [] };

export type LoadResult = {
  blob: ConfigBlob;
  /** Set when the checklists could not be read or written. Never fatal to the call UI. */
  problem: string | null;
};

const VALID_TYPES: FieldType[] = ['string', 'number', 'date', 'boolean', 'enum'];

function isField(value: unknown): value is ChecklistField {
  if (typeof value !== 'object' || value === null) return false;
  const field = value as Record<string, unknown>;
  return (
    typeof field.id === 'string' &&
    field.id.length > 0 &&
    typeof field.label === 'string' &&
    typeof field.description === 'string' &&
    typeof field.type === 'string' &&
    VALID_TYPES.includes(field.type as FieldType) &&
    (field.enumValues === undefined ||
      (Array.isArray(field.enumValues) && field.enumValues.every((v) => typeof v === 'string')))
  );
}

function isConfig(value: unknown): value is ChecklistConfig {
  if (typeof value !== 'object' || value === null) return false;
  const config = value as Record<string, unknown>;
  return (
    typeof config.id === 'string' &&
    config.id.length > 0 &&
    typeof config.name === 'string' &&
    (config.builtinKey == null || typeof config.builtinKey === 'string') &&
    Array.isArray(config.fields) &&
    config.fields.every(isField)
  );
}

/**
 * Fills in the fields of a checklist the app defines itself.
 *
 * The endpoint sends the row - id, name, builtinKey - and no fields, because
 * they are code. Substituting them here means everything downstream, from the
 * panel to the extractor to the saved record, sees an ordinary checklist.
 */
function withBuiltinFields(config: ChecklistConfig): ChecklistConfig {
  if (config.builtinKey !== PRICING_BUILTIN_KEY) return config;
  return { ...config, name: config.name || PRICING_CONFIG_NAME, fields: PRICING_FIELDS };
}

/**
 * Exported for tests: the shape guard that decides whether a response is usable.
 *
 * The endpoint and this app are versioned separately and deployed separately,
 * so a response can be from a build that disagrees with this one. Rejecting the
 * whole payload beats rendering half a checklist.
 */
export function parseResponse(parsed: unknown): LoadResult {
  if (typeof parsed !== 'object' || parsed === null) {
    return { blob: { ...EMPTY }, problem: 'Checklists came back in an unexpected format.' };
  }

  const body = parsed as Record<string, unknown>;
  if (!Array.isArray(body.configs) || !body.configs.every(isConfig)) {
    return { blob: { ...EMPTY }, problem: 'Checklists came back malformed.' };
  }

  const configs = (body.configs as ChecklistConfig[]).map(withBuiltinFields);
  // Mirrored from the server, not repaired. Selection is a column on the
  // checklist's own row there, so it cannot dangle - unlike the localStorage
  // blob this replaces, where a deleted checklist could leave a stale id.
  const selected =
    typeof body.selectedConfigId === 'string' &&
    configs.some((config) => config.id === body.selectedConfigId)
      ? body.selectedConfigId
      : null;

  return { blob: { selectedConfigId: selected, configs }, problem: null };
}

function url(path = ''): string {
  return `${endpoints.checklists.replace(/\/+$/, '')}/${path}`;
}

type Reply = LoadResult & { raw: unknown };

/** Turns anything that can go wrong with a request into one sentence. */
async function request(path: string, init?: RequestInit): Promise<Reply> {
  if (!endpoints.checklists) {
    return {
      blob: { ...EMPTY },
      problem: 'This build has no checklists endpoint, so checklists cannot be loaded or saved.',
      raw: null,
    };
  }

  let response: Response;
  try {
    response = await fetch(url(path), {
      ...init,
      headers: init?.body ? { 'content-type': 'application/json' } : undefined,
    });
  } catch {
    return { blob: { ...EMPTY }, problem: 'Could not reach the checklist service.', raw: null };
  }

  let parsed: unknown = null;
  try {
    parsed = await response.json();
  } catch {
    parsed = null;
  }

  if (!response.ok) {
    // The endpoint's 400s name what is wrong with the checklist, which is worth
    // showing verbatim. Anything else gets a status the user can quote.
    const detail =
      typeof (parsed as Record<string, unknown> | null)?.error === 'string'
        ? String((parsed as Record<string, unknown>).error)
        : `The checklist service returned ${response.status}.`;
    return { blob: { ...EMPTY }, problem: detail, raw: parsed };
  }

  return { ...parseResponse(parsed), raw: parsed };
}

export const ConfigStore = {
  /**
   * Read once when the inspector view mounts. The server is the durable copy;
   * what the app holds is a working copy.
   */
  async load(): Promise<LoadResult> {
    const result = await request('');
    if (result.problem || result.blob.configs.length > 0) return result;
    // Nothing on the server yet - this may be the first load after the move.
    return migrateLegacy();
  },

  /** Create a checklist and return the state that includes it. */
  async create(name: string): Promise<LoadResult & { created: ChecklistConfig | null }> {
    const result = await request('', { method: 'POST', body: JSON.stringify({ name, fields: [] }) });
    // Found by the id the endpoint reports, not by name: two checklists may
    // share a name, and only the response knows which row was just written.
    const id = (result.raw as Record<string, unknown> | null)?.id;
    const created =
      typeof id === 'string'
        ? (result.blob.configs.find((config) => config.id === id) ?? null)
        : null;
    return { ...result, created };
  },

  /** Replace a checklist's name and fields. */
  save(config: ChecklistConfig): Promise<LoadResult> {
    return request(config.id, {
      method: 'PUT',
      body: JSON.stringify({ name: config.name, fields: config.fields }),
    });
  },

  remove(id: string): Promise<LoadResult> {
    return request(id, { method: 'DELETE' });
  },

  /** Choose the checklist calls use. */
  select(id: string | null): Promise<LoadResult> {
    return request('selected', { method: 'POST', body: JSON.stringify({ id }) });
  },
};

/**
 * One-time lift of whatever this browser still has in localStorage.
 *
 * Someone has been using this app with checklists that only ever existed in
 * their browser. Moving storage to the cloud should not quietly throw those
 * away on the next page load.
 *
 * Runs only when the server has none, so it cannot append duplicates to a set
 * someone else already migrated, and marks the local key done either way so a
 * failed upload does not retry on every load. Two people migrating different
 * browsers into an empty database at the same moment would merge both sets -
 * unlikely, harmless, and easier to tidy up than a silent data loss.
 */
async function migrateLegacy(): Promise<LoadResult> {
  let raw: string | null = null;
  try {
    raw = window.localStorage.getItem(LEGACY_STORAGE_KEY);
  } catch {
    return { blob: { ...EMPTY }, problem: null }; // storage disabled; nothing to lift
  }
  if (!raw) return { blob: { ...EMPTY }, problem: null };

  let legacy: ConfigBlob & { version?: number };
  try {
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    if (parsed.version !== STORAGE_VERSION || !Array.isArray(parsed.configs)) throw new Error();
    legacy = parsed as unknown as ConfigBlob & { version?: number };
  } catch {
    try {
      window.localStorage.removeItem(LEGACY_STORAGE_KEY);
    } catch {
      /* nothing worth saying */
    }
    return { blob: { ...EMPTY }, problem: null };
  }

  const usable = legacy.configs.filter(isConfig).filter((config) => config.fields.length > 0);
  if (usable.length === 0) {
    try {
      window.localStorage.removeItem(LEGACY_STORAGE_KEY);
    } catch {
      /* nothing worth saying */
    }
    return { blob: { ...EMPTY }, problem: null };
  }

  let state: LoadResult = { blob: { ...EMPTY }, problem: null };
  for (const config of usable) {
    const created = await ConfigStore.create(config.name);
    if (created.problem || !created.created) {
      return {
        blob: created.blob,
        problem: 'Some checklists saved in this browser could not be copied to the cloud.',
      };
    }
    state = await ConfigStore.save({ ...config, id: created.created.id });
    if (state.problem) return state;
    // Whichever was in use in the browser stays in use. Nothing was marked in
    // use before this ran, so the first one through wins if none was.
    if (config.id === legacy.selectedConfigId || state.blob.selectedConfigId === null) {
      state = await ConfigStore.select(created.created.id);
    }
  }

  try {
    window.localStorage.removeItem(LEGACY_STORAGE_KEY);
  } catch {
    /* nothing worth saying */
  }
  return state;
}
